import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface PointEvent {
 id: string;
 amount: number;
 reason: string;
}

export const PointsAwarded = () => {
 const { user } = useAuth();
 const [events, setEvents] = useState<PointEvent[]>([]);

 useEffect(() => {
 if (!user) return;

 let channel: ReturnType<typeof supabase.channel> | null = null;

 try {
 channel = supabase
 .channel("points_logs_premium")
 .on(
 "postgres_changes",
 {
 event: "INSERT",
 schema: "public",
 table: "points_logs",
 filter: `user_id=eq.${user.id}`,
 },
 (payload) => {
 const newEvent: PointEvent = {
 id: payload.new.id,
 amount: payload.new.amount,
 reason: payload.new.reason,
 };
 setEvents((prev) => [...prev, newEvent]);

 setTimeout(() => {
 setEvents((prev) => prev.filter((e) => e.id !== newEvent.id));
 }, 2000);
 }
 )
 .subscribe((status) => {
 if (status === "CHANNEL_ERROR") {
 console.warn("[PointsAwarded] Realtime channel error — tabela points_logs pode não existir ainda.");
 }
 });
 } catch (err) {
 console.warn("[PointsAwarded] Failed to set up realtime (non-fatal):", err);
 }

 return () => {
 if (channel) supabase.removeChannel(channel);
 };
 }, [user]);

  return (
    <>
      {/* ─── EFEITO DE LUZ (TOP EDGE GLOW) QUANDO HOUVER EVENTO ─── */}
      <AnimatePresence>
        {events.length > 0 && (
          <motion.div
            key="top-glow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed top-0 left-0 w-full h-[300px] pointer-events-none z-[9998]"
            style={{
              background: `
                radial-gradient(ellipse at 0% 0%, rgba(255,106,0,0.8) 0%, transparent 60%),
                radial-gradient(ellipse at 100% 0%, rgba(255,106,0,0.8) 0%, transparent 60%),
                linear-gradient(to bottom, rgba(255,106,0,0.2) 0%, transparent 100%)
              `
            }}
          />
        )}
      </AnimatePresence>

      <div className="fixed inset-0 pointer-events-none z-[9999] flex items-center justify-center p-4">
        <AnimatePresence>
          {events.map((event) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ 
                opacity: [0, 1, 1, 0], 
                scale: [0.5, 1.2, 1, 0.9],
              }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 3, ease: "easeOut", times: [0, 0.2, 0.8, 1] }}
              className="flex flex-col items-center gap-3 absolute"
            >
              <div className="relative max-w-[90vw]">
                {/* Premium Glow & Aura */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.4, 0.8, 0.4]
                  }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="absolute inset-0 bg-[#FF6A00] blur-[30px] rounded-full"
                />
                
                <div className="relative flex items-center gap-3 md:gap-4 bg-gradient-to-br from-[#FF6A00] to-[#FF8C33] text-black px-6 py-3 md:px-8 md:py-4 rounded-full shadow-[0_20px_50px_rgba(255,106,0,0.6)] border border-white/20 backdrop-blur-sm">
                  <Coins className="w-6 h-6 md:w-8 md:h-8 animate-bounce fill-black/20" />
                  <div className="flex flex-col">
                    <span className="text-3xl md:text-4xl font-black font-display tracking-tighter leading-none">
                      +{event.amount}
                    </span>
                    <span className="text-[9px] md:text-[10px] uppercase font-black tracking-widest opacity-80 mt-1">
                      {event.reason || "Nova Venda Realizada"}
                    </span>
                  </div>
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 absolute -top-1 -right-1 md:-top-2 md:-right-2 text-yellow-300 animate-pulse" />
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
};
